/**
 * Application Layer - FilterByTimeRange UseCase
 * 時間帯フィルタリングユースケース
 */

import type {
  CsvData,
  TimeRange,
} from '../../../domain/medicalReservation';
import {
  createCsvData,
  isTimeRangeComplete,
  isDateTimeStringWithinTimeRange,
  CSV_COLUMN_NAMES,
  getColumnIndex,
} from '../../../domain/medicalReservation';

/**
 * フィルタリング結果
 */
export interface FilterResult {
  /** 時間内データ */
  readonly filteredData: CsvData;
  /** 時間外データ */
  readonly outsideTimeRangeData: CsvData;
  /** 時間内件数 */
  readonly filteredCount: number;
  /** 時間外件数 */
  readonly outsideCount: number;
}

/**
 * データを時間帯でフィルタリングするユースケース
 * @param csvData 元のCSVデータ
 * @param timeRange 時間範囲
 * @returns フィルタリング結果
 */
export const filterByTimeRange = (
  csvData: CsvData,
  timeRange: TimeRange
): FilterResult => {
  // 時間範囲が完全でない場合は全データを時間内として返す
  if (!isTimeRangeComplete(timeRange)) {
    return {
      filteredData: csvData,
      outsideTimeRangeData: createCsvData(
        [...csvData.headers],
        [],
        csvData.fileName
      ),
      filteredCount: csvData.totalRows,
      outsideCount: 0,
    };
  }

  // 診療開始時間列のインデックスを取得
  const timeColumnIndex = getColumnIndex(csvData, CSV_COLUMN_NAMES.CONSULTATION_DATE_TIME);

  if (timeColumnIndex === -1) {
    // 診療開始時間列が見つからない場合は全データを時間内として返す
    console.warn('診療開始時間列が見つかりません');
    return {
      filteredData: csvData,
      outsideTimeRangeData: createCsvData(
        [...csvData.headers],
        [],
        csvData.fileName
      ),
      filteredCount: csvData.totalRows,
      outsideCount: 0,
    };
  }

  const filteredRows: string[][] = [];
  const outsideRows: string[][] = [];

  // 各行をフィルタリング
  for (const row of csvData.rows) {
    const timeValue = row[timeColumnIndex];

    if (!timeValue) {
      // 時間値が空の場合は時間外として扱う
      outsideRows.push([...row]);
      continue;
    }

    const isInRange = isDateTimeStringWithinTimeRange(timeValue, timeRange);

    if (isInRange) {
      filteredRows.push([...row]);
    } else {
      outsideRows.push([...row]);
    }
  }

  return {
    filteredData: createCsvData(
      [...csvData.headers],
      filteredRows,
      csvData.fileName
    ),
    outsideTimeRangeData: createCsvData(
      [...csvData.headers],
      outsideRows,
      csvData.fileName
    ),
    filteredCount: filteredRows.length,
    outsideCount: outsideRows.length,
  };
};

/**
 * 時間外データのみを抽出するユースケース
 * @param csvData 元のCSVデータ
 * @param timeRange 時間範囲
 * @returns 時間外データ
 */
export const extractOutsideTimeRangeData = (
  csvData: CsvData,
  timeRange: TimeRange
): CsvData => {
  const result = filterByTimeRange(csvData, timeRange);
  return result.outsideTimeRangeData;
};

/**
 * 時間内データのみを抽出するユースケース
 * @param csvData 元のCSVデータ
 * @param timeRange 時間範囲
 * @returns 時間内データ
 */
export const extractWithinTimeRangeData = (
  csvData: CsvData,
  timeRange: TimeRange
): CsvData => {
  const result = filterByTimeRange(csvData, timeRange);
  return result.filteredData;
};
